import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://b.supchats.com';

const Truncated = ({ text, max = 80 }) => {
  if (!text) return <span style={{ color: '#9ca3af' }}>—</span>;
  if (text.length <= max) return <span>{text}</span>;
  const short = text.slice(0, max) + '…';
  return (
    <span className="truncated-text-container">
      <span className="truncated-text">{short}</span>
      <span className="truncated-tooltip">{text}</span>
    </span>
  );
};

const StatusBadge = ({ status, hasApiKey }) => {
  if (hasApiKey) {
    return <span className="badge badge-success">Ключ выдан</span>;
  }
  if (status === 'approved') {
    return <span className="badge badge-success">Одобрено</span>;
  }
  if (status === 'rejected') {
    return <span className="badge badge-danger">Отклонено</span>;
  }
  return <span className="badge badge-warning">В ожидании</span>;
};

const ApiRequestsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const loadData = async () => {
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API_URL}/admin/api-access-requests`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      if (res.data && res.data.ok) {
        setItems(res.data.items || []);
      } else {
        setMessage(res.data?.detail || 'Ошибка загрузки заявок');
      }
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Ошибка сети при загрузке заявок');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const formatDateTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const handleApprove = async (id) => {
    setActionLoadingId(id);
    setMessage('');
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(
        `${API_URL}/admin/api-access-requests/${id}/approve`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      if (res.data && res.data.ok) {
        setMessage('API-ключ выдан пользователю.');
        await loadData();
      } else {
        setMessage(res.data?.detail || 'Ошибка при выдаче API-ключа');
      }
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Ошибка при выдаче API-ключа');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReject = async (id) => {
    const confirmed = window.confirm('Отклонить запрос на доступ к API?');
    if (!confirmed) return;

    setActionLoadingId(id);
    setMessage('');
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(
        `${API_URL}/admin/api-access-requests/${id}/reject`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      if (res.data && res.data.ok) {
        setMessage('Запрос отклонён.');
        await loadData();
      } else {
        setMessage(res.data?.detail || 'Ошибка при отклонении заявки');
      }
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Ошибка при отклонении заявки');
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <div>
          <h2>Заявки на доступ к API</h2>
          <p className="admin-section-subtitle">
            Здесь отображаются запросы от пользователей на выдачу API-ключа. Как правило, это пользователи
            с бесплатным тарифом. После одобрения для пользователя создаётся API-ключ.
          </p>
        </div>
        <button
          type="button"
          className="contact-log-btn"
          onClick={loadData}
          disabled={loading}
        >
          Обновить
        </button>
      </div>

      {message && (
        <div className="admin-message info">
          {message}
        </div>
      )}

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Дата</th>
              <th>Email</th>
              <th>Тариф</th>
              <th>Контактные данные</th>
              <th>Статус</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '16px' }}>
                  Пока нет заявок на доступ к API.
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={item.id}>
                <td>{formatDateTime(item.created_at)}</td>
                <td>{item.email}</td>
                <td>{item.user_role || '—'}</td>
                <td>
                  <Truncated text={item.contact_details} />
                </td>
                <td>
                  <StatusBadge status={item.status} hasApiKey={item.has_api_key} />
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className="contact-log-btn"
                      onClick={() => handleApprove(item.id)}
                      disabled={
                        loading ||
                        actionLoadingId === item.id ||
                        item.status === 'approved' ||
                        item.has_api_key
                      }
                    >
                      {actionLoadingId === item.id ? 'Обработка...' : 'Выдать API'}
                    </button>
                    <button
                      type="button"
                      className="contact-log-btn delete"
                      onClick={() => handleReject(item.id)}
                      disabled={
                        loading ||
                        actionLoadingId === item.id ||
                        item.status === 'rejected'
                      }
                    >
                      Отклонить
                    </button>
                  </div>
                </td>
              </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ApiRequestsPage;

